// ============================================================================
// 바인더 렌더 — three.js. BinderRS 만 읽는다. 물리를 모른다.
//
// 레이어 (BINDER_DESIGN §9):
//   커버 → left/right 시트 더미 → 각 면(바탕 → 카드 9 → 배지 9 → 슬리브 앞판)
//   → turning 시트 → 수납 카드 → 디밍 평면 → 상세 카드 A/B
//
// **카메라는 안 움직인다.** 바인더 루트를 -viewX 만큼 옮긴다 — 수학적으로 같고
// 개봉 화면의 좌표계(카메라 x=0 전제)를 건드리지 않는다.
//
// 텍스처는 **카드당 파일 하나**다 (2026-09-10 면 아틀라스 제거). 칸에는 줄여서 올리고
// 상세 보기는 같은 파일을 원본 크기로 다시 디코딩한다. GPU 상주는 현재 면 ±N 창 + LRU 로
// 카드 수와 무관하게 고정된다 (상한은 P.binder.cellTex* / detailTexMax).
// ============================================================================
import * as THREE from 'three';
import { P, WORLD, BINDER_LAYERS, CARD_COUNT } from './params';
import { BinderRS } from './binder-sim';
import { CARD_ASPECT, CELLS_PER_FACE, CardDef, cardArtPath, cardById, cardBySlot, faceCount, faceToSlot, sheetCount } from './cards.ts';
import { makeSurfaceMaterial, SurfaceUniforms } from './geometry';
import {
  TexSet,
  assetUrl,
  cellTextureFromBlob,
  fallbackBinderCover,
  fallbackBinderGloss,
  fallbackBinderSheet,
  fallbackDetailCard,
  fallbackFaceCard,
  fetchAssetBlob,
  makeBadgeTexture,
  textureFromBlob,
} from './assets';
import { SEG_FLIP, SEG_PAN, binderMetaVersion, cardSizeInCell, cellBox, CellBox, faceHeight, faceWidth, isSpread, perspScale, screenWidth, thetaOf, viewXOf } from './binder-layout';

export type DetailTexSource = 'original' | 'cell' | 'back' | 'fallback' | 'none';
/** 같은 파일에서 갈라져 나오는 텍스처의 종류. 도착 통보가 어느 쪽인지 구분한다. */
export type CardTexTier = 'cell' | 'detail' | 'open';

const RANK_LABEL = ['C', 'R', 'AR', 'UR'];
const FALLBACK_DETAIL_MAX = 8;

// ------------------------------------------------------------------ 텍스처 캐시
/**
 * 카드 그림 캐시 — **카드당 파일 하나** (`cardArtPath`, 기본은 `cards/<id>.webp`).
 *
 * 파일은 blob 으로 **한 번만** 받고, 거기서 세 가지 텍스처를 만든다:
 *   칸용   — `P.binder.cellTexWidth` 로 줄인 것. 원본(768w)을 45장 올리면 GPU 가 감당 못 한다
 *   상세용 — 원본 크기. 칸 텍스처를 확대하면 글자가 뭉개진다
 *   개봉용 — `P.open.cardTexWidth` 로 줄인 것. 개봉 화면 카드는 칸보다 크게 보인다
 * 아틀라스 시절엔 이 둘이 **서로 다른 파일**이라 같은 그림을 두 번 받았다.
 *
 * **개봉용만 LRU 가 아니다.** 한 팩은 8장으로 고정이고 8장이 동시에 보이므로 버릴 것이 없다 —
 * 대신 `releaseOpen()` 이 **직전 팩** 것을 새 팩 것을 요청한 뒤에 버린다 (같은 카드가 또 나오면
 * 파일을 다시 받지 않게 하려고 순서가 그렇다).
 *
 * 요청·해제는 **스텝 밖**(렌더 프레임)에서만 한다. 404 (그림이 아직 없는 카드) 는
 * 기억해 두고 다시 요청하지 않는다.
 */
export class CardTextureCache {
  private blobs = new Map<string, Blob>();
  private inflight = new Map<string, Promise<Blob | null>>();
  private cell = new Map<string, THREE.Texture>();
  private detail = new Map<string, THREE.Texture>();
  private open = new Map<string, THREE.Texture>();
  private cellUsed = new Map<string, number>();
  private detailUsed = new Map<string, number>();
  private missing = new Set<string>();
  private pendingCell = new Set<string>();
  private pendingDetail = new Set<string>();
  private pendingOpen = new Set<string>();
  private openWidth = 0;
  private clock = 0;
  /** ok=false 면 404. 어느 쪽이든 재질을 다시 고르게 해야 한다. */
  onArrive: ((id: string, ok: boolean, tier: CardTexTier) => void) | null = null;
  loads = 0;

  /** 파일 하나 = 요청 하나. 칸과 상세가 같은 약속을 나눠 갖는다. */
  private blobOf(id: string): Promise<Blob | null> {
    const b = this.blobs.get(id);
    if (b) return Promise.resolve(b);
    if (this.missing.has(id)) return Promise.resolve(null);
    let p = this.inflight.get(id);
    if (p) return p;
    const def = cardById(id);
    this.loads++;
    p = fetchAssetBlob(assetUrl(def ? cardArtPath(def) : `cards/${id}.webp`)).then((blob) => {
      this.inflight.delete(id);
      if (!blob) {
        this.missing.add(id);
        return null;
      }
      this.blobs.set(id, blob);
      return blob;
    });
    this.inflight.set(id, p);
    return p;
  }

  /** 칸에 올릴 축소 텍스처. 아직 없으면 null (부르는 쪽이 폴백을 쓰고, 도착하면 onArrive). */
  requestCell(id: string): THREE.Texture | null {
    const t = this.cell.get(id);
    if (t) {
      this.cellUsed.set(id, ++this.clock);
      return t;
    }
    if (this.missing.has(id) || this.pendingCell.has(id)) return null;
    this.pendingCell.add(id);
    const w = Math.max(32, P.binder.cellTexWidth | 0);
    void this.blobOf(id)
      .then((blob) => (blob ? cellTextureFromBlob(blob, w, Math.round(w * CARD_ASPECT)) : null))
      .then((tex) => {
        this.pendingCell.delete(id);
        if (!tex) {
          this.onArrive?.(id, false, 'cell');
          return;
        }
        this.cell.set(id, tex);
        this.cellUsed.set(id, ++this.clock);
        this.onArrive?.(id, true, 'cell');
      });
    return null;
  }

  /** 상세 보기용 원본 크기 텍스처. 칸 텍스처가 이미 있으면 파일을 다시 받지 않는다. */
  requestDetail(id: string): THREE.Texture | null {
    const t = this.detail.get(id);
    if (t) {
      this.detailUsed.set(id, ++this.clock);
      return t;
    }
    if (this.missing.has(id) || this.pendingDetail.has(id)) return null;
    this.pendingDetail.add(id);
    void this.blobOf(id)
      .then((blob) => (blob ? textureFromBlob(blob) : null))
      .then((tex) => {
        this.pendingDetail.delete(id);
        if (!tex) {
          this.onArrive?.(id, false, 'detail');
          return;
        }
        this.detail.set(id, tex);
        this.detailUsed.set(id, ++this.clock);
        this.onArrive?.(id, true, 'detail');
      });
    return null;
  }

  /**
   * 개봉 화면에 올릴 축소 텍스처 (폭 `w`). 아직 없으면 null — 부르는 쪽이 **등급 그림으로 시작해서**
   * 도착하면 `onArrive('open')` 에서 갈아끼운다 (로딩 화면 금지).
   *
   * 폭이 바뀌면(`P.open.cardTexWidth` 슬라이더) 갖고 있던 것을 전부 버리고 다시 만든다 —
   * 칸 텍스처와 달리 8장뿐이라 다시 받는 비용이 작고, 반쪽만 새 폭이면 카드마다 선명도가 달라진다.
   */
  requestOpen(id: string, w: number): THREE.Texture | null {
    const cw = Math.max(32, w | 0);
    if (this.openWidth !== cw) {
      this.openWidth = cw;
      const stale = [...this.open.keys()];
      for (const t of this.open.values()) t.dispose();
      this.open.clear();
      for (const oid of stale) this.dropBlob(oid);
    }
    const t = this.open.get(id);
    if (t) return t;
    if (this.missing.has(id) || this.pendingOpen.has(id)) return null;
    this.pendingOpen.add(id);
    void this.blobOf(id)
      .then((blob) => (blob ? cellTextureFromBlob(blob, cw, Math.round(cw * CARD_ASPECT)) : null))
      .then((tex) => {
        this.pendingOpen.delete(id);
        if (!tex) {
          this.onArrive?.(id, false, 'open');
          return;
        }
        // 그 사이에 폭이 바뀌었으면 버린다 (다음 요청이 새 폭으로 다시 만든다).
        if (this.openWidth !== cw) {
          tex.dispose();
          return;
        }
        this.open.set(id, tex);
        this.onArrive?.(id, true, 'open');
      });
    return null;
  }

  /**
   * `keep` 에 없는 개봉용 텍스처를 전부 버린다 = **직전 팩 8장 해제.**
   * 새 팩 8장을 `requestOpen` 으로 요청한 **뒤에** 부를 것 — 먼저 부르면 같은 카드가
   * 또 나왔을 때 방금 버린 것을 다시 받는다.
   */
  releaseOpen(keep: ReadonlySet<string>): void {
    for (const [id, t] of [...this.open]) {
      if (keep.has(id)) continue;
      t.dispose();
      this.open.delete(id);
      this.dropBlob(id);
    }
  }

  /** 창(keep) 밖이면서 상한을 넘은 것부터 dispose. 렌더 프레임 안에서만 부른다. */
  evictCells(keep: ReadonlySet<string>, max: number): void {
    if (this.cell.size <= max) return;
    const cand = [...this.cell.keys()].filter((id) => !keep.has(id));
    cand.sort((a, b) => (this.cellUsed.get(a) ?? 0) - (this.cellUsed.get(b) ?? 0));
    for (const id of cand) {
      if (this.cell.size <= max) break;
      this.cell.get(id)!.dispose();
      this.cell.delete(id);
      this.cellUsed.delete(id);
      this.dropBlob(id);
    }
  }

  evictDetails(max: number): void {
    if (this.detail.size <= max) return;
    const cand = [...this.detail.keys()].sort((a, b) => (this.detailUsed.get(a) ?? 0) - (this.detailUsed.get(b) ?? 0));
    for (const id of cand) {
      if (this.detail.size <= max) break;
      this.detail.get(id)!.dispose();
      this.detail.delete(id);
      this.detailUsed.delete(id);
      this.dropBlob(id);
    }
  }

  /** 파생 텍스처가 하나도 안 남았으면 압축된 원본도 들고 있을 이유가 없다. */
  private dropBlob(id: string): void {
    if (!this.cell.has(id) && !this.detail.has(id) && !this.open.has(id)) this.blobs.delete(id);
  }

  residentCells(): string[] {
    return [...this.cell.keys()];
  }
  residentDetails(): string[] {
    return [...this.detail.keys()];
  }
  residentOpen(): string[] {
    return [...this.open.keys()];
  }
  missed(id: string): boolean {
    return this.missing.has(id);
  }
}

// ------------------------------------------------------------------ 작은 도구
function quadGeo(): THREE.PlaneGeometry {
  return new THREE.PlaneGeometry(1, 1);
}
/** map 이 실제로 바뀔 때만 needsUpdate — 그냥 켜면 셰이더가 다시 컴파일된다. */
function setMap(mat: THREE.MeshBasicMaterial, t: THREE.Texture): void {
  if (mat.map === t) return;
  mat.map = t;
  mat.needsUpdate = true;
}

// ------------------------------------------------------------------ 면 하나
/** 시트 한 쪽. 바탕 1 + 카드 쿼드 9 + 배지 9 + 앞판 1. */
class FaceHolder {
  group = new THREE.Group();
  base: THREE.Mesh;
  gloss: THREE.Mesh;
  cards: THREE.Mesh[] = [];
  badges: THREE.Mesh[] = [];
  face = 0;
  private stamp = '';

  constructor(private back: boolean, sheetTex: THREE.Texture, glossTex: THREE.Texture) {
    this.base = new THREE.Mesh(quadGeo(), makeSurfaceMaterial(sheetTex, 0.15, 0.1));
    this.base.position.z = BINDER_LAYERS.sheetBase;
    this.group.add(this.base);
    for (let i = 0; i < CELLS_PER_FACE; i++) {
      const m = new THREE.Mesh(quadGeo(), new THREE.MeshBasicMaterial({ transparent: true, alphaTest: 0.4 }));
      m.position.z = BINDER_LAYERS.card;
      m.visible = false;
      this.cards.push(m);
      this.group.add(m);
      const b = new THREE.Mesh(quadGeo(), new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false }));
      b.position.z = BINDER_LAYERS.badge;
      b.renderOrder = 8;
      b.visible = false;
      this.badges.push(b);
      this.group.add(b);
    }
    const gm = makeSurfaceMaterial(glossTex, 0.1, 0.0);
    (gm.uniforms as unknown as SurfaceUniforms).alphaCut.value = 0;
    gm.depthWrite = false; // on 이면 배지가 잘린다
    this.gloss = new THREE.Mesh(quadGeo(), gm);
    this.gloss.position.z = BINDER_LAYERS.gloss;
    this.gloss.renderOrder = 10;
    this.group.add(this.gloss);
    this.group.rotation.y = back ? Math.PI : 0;
  }

  setSheetTexture(t: THREE.Texture): void {
    (this.base.material as THREE.ShaderMaterial).uniforms.map.value = t;
  }
  setGlossTexture(t: THREE.Texture): void {
    (this.gloss.material as THREE.ShaderMaterial).uniforms.map.value = t;
  }
  setBand(pos: number, strength: number): void {
    const u = (this.gloss.material as THREE.ShaderMaterial).uniforms as unknown as SurfaceUniforms;
    u.bandPos.value = pos;
    u.bandWidth.value = P.binder.glossBandWidth;
    u.bandStrength.value = strength;
  }

  /** 칸 좌표를 다시 잡는다. 폭·높이·측정 좌표가 바뀌면 반드시 호출 (폴백→측정 교체 함정). */
  layout(W: number, H: number, box: CellBox): void {
    this.base.scale.set(W, H, 1);
    this.gloss.scale.set(W, H, 1);
    for (let i = 0; i < CELLS_PER_FACE; i++) {
      cellBox(i, this.back, W, H, box);
      const cs = { w: 0, h: 0 };
      cardSizeInCell(box, CARD_ASPECT, cs);
      const c = this.cards[i];
      c.position.set(box.cx, box.cy, BINDER_LAYERS.card);
      // 쿼드 = **카드 한 장**. 아틀라스 시절엔 쿼드가 칸이고 인셋이 그림에 구워져 있었지만
      // 이제 텍스처가 카드 그림 그대로라 인셋을 여기서 준다 (cardSizeInCell = 앱·도구 공용 규칙).
      c.scale.set(cs.w, cs.h, 1);
      const b = this.badges[i];
      const bs = P.binder.badgeSize * box.w;
      b.scale.set(bs, bs, 1);
      b.position.set(box.cx + box.w / 2 - bs * 0.45, box.cy - cs.h / 2 + bs * 0.45, BINDER_LAYERS.badge);
    }
    this.stamp = ''; // 좌표가 바뀌었으니 UV 도 다시
  }

  /** 이 홀더가 그릴 면을 정한다. 세그먼트 시작과 정착 때만 부른다. */
  assign(face: number, view: BinderView, force = false): void {
    const F = faceCount();
    if (face < 1 || face > F) {
      this.group.visible = false;
      this.face = 0;
      return;
    }
    this.group.visible = true;
    const stamp = `${face}|${view.countsVersion}|${view.texVersion}|${binderMetaVersion()}`;
    if (!force && stamp === this.stamp && this.face === face) return;
    this.stamp = stamp;
    this.face = face;
    for (let i = 0; i < CELLS_PER_FACE; i++) {
      const slot = faceToSlot(face, i);
      const def = cardBySlot(slot);
      const count = view.countOf(def, slot);
      const card = this.cards[i];
      const mat = card.material as THREE.MeshBasicMaterial;
      if (!def || count <= 0) {
        card.visible = false;
        this.badges[i].visible = false;
        continue;
      }
      card.visible = true;
      setMap(mat, view.cardCache.requestCell(def.id) ?? view.fallbackCardTex(def));
      const badge = this.badges[i];
      if (count >= 2) {
        badge.visible = true;
        setMap(badge.material as THREE.MeshBasicMaterial, view.badgeTex(count));
      } else {
        badge.visible = false;
      }
    }
  }
}

/** 시트 한 장 = 앞면 홀더 + 뒷면 홀더. 등을 맞대고 sheetThickness 만큼 떨어져 있다. */
class SheetGroup {
  group = new THREE.Group();
  front: FaceHolder;
  back: FaceHolder;
  sheet = 0;

  constructor(tex: TexSet) {
    this.front = new FaceHolder(false, tex.binderSheet, tex.binderGloss);
    this.back = new FaceHolder(true, tex.binderSheetBack, tex.binderGlossBack);
    this.group.add(this.front.group, this.back.group);
  }

  layout(W: number, H: number, box: CellBox): void {
    const t = P.binder.sheetThickness;
    this.front.group.position.set(W / 2, P.binder.faceY, t / 2);
    this.back.group.position.set(W / 2, P.binder.faceY, -t / 2);
    this.front.layout(W, H, box);
    this.back.layout(W, H, box);
  }

  /** 시트 번호를 배정한다. 앞면 = 2s-1, 뒷면 = 2s. **교대와 숨김을 한 함수에서** 한다. */
  assign(s: number, view: BinderView): void {
    this.sheet = s;
    if (s < 1 || s > sheetCount()) {
      this.group.visible = false;
      return;
    }
    this.group.visible = true;
    this.front.assign(2 * s - 1, view);
    this.back.assign(2 * s, view);
  }
}

// ------------------------------------------------------------------ 뷰
export class BinderView {
  root = new THREE.Group();
  cover: THREE.Mesh;
  left: SheetGroup;
  right: SheetGroup;
  turning: SheetGroup;
  stowQuads: THREE.Mesh[] = [];
  dim: THREE.Mesh;
  detailA: THREE.Mesh;
  detailB: THREE.Mesh;
  cardCache = new CardTextureCache();
  countsVersion = 0;
  /** 카드 그림이 도착할 때마다 오른다. 홀더의 stamp 에 들어가서 **그 프레임에 다시 입힌다**. */
  texVersion = 0;
  counts: Record<string, number> = {};
  /** 상세 보기 원본이 도착했을 때 (진단·검증 도구가 detail-tex 이벤트로 본다). */
  onDetailTex: ((id: string) => void) | null = null;
  /** 수납 중 아직 안 내려앉은 카드는 칸에서 빼고 센다 — 배지 숫자가 착지할 때마다 올라간다. */
  private pendingStow = new Map<number, number>();
  detailSourceA: DetailTexSource = 'none';
  private badgeCache = new Map<number, THREE.Texture>();
  /** 등급당 하나. id 마다 만들면 카드 수에 비례해 캔버스 텍스처가 쌓인다 (§7.2 의 정신). */
  private fallbackCards = new Map<number, THREE.Texture>();
  /** 상세 보기 폴백은 id 를 보여줘야 쓸모가 있어서 id 별로 두되 **상한**을 건다. */
  private fallbackDetails = new Map<string, THREE.Texture>();
  private box: CellBox = { cx: 0, cy: 0, w: 0, h: 0 };
  private lastW = -1;
  private lastH = -1;
  private lastMeta = -1;
  private realTex = { cover: false, sheet: false, sheetBack: false, gloss: false, glossBack: false };
  /** 지금 창(현재 면 ±cellTexWindow) 안에 있는, 보유한 카드의 id. 여기 있는 것은 안 버린다. */
  private windowIds = new Set<string>();
  private windowStamp = '';

  constructor(private tex: TexSet) {
    this.cover = new THREE.Mesh(quadGeo(), makeSurfaceMaterial(tex.binderCover, 0.2, 0.1));
    this.cover.position.z = BINDER_LAYERS.cover;
    this.root.add(this.cover);
    this.left = new SheetGroup(tex);
    this.right = new SheetGroup(tex);
    this.turning = new SheetGroup(tex);
    this.left.group.rotation.y = -Math.PI;
    this.root.add(this.left.group, this.right.group, this.turning.group);

    for (let i = 0; i < CARD_COUNT; i++) {
      const m = new THREE.Mesh(quadGeo(), new THREE.MeshBasicMaterial({ transparent: true, alphaTest: 0.3 }));
      m.position.z = BINDER_LAYERS.stowCard;
      m.visible = false;
      m.renderOrder = 15;
      this.stowQuads.push(m);
      this.root.add(m);
    }

    this.dim = new THREE.Mesh(quadGeo(), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0, depthWrite: false }));
    this.dim.position.z = BINDER_LAYERS.dim;
    this.dim.renderOrder = 20;
    this.dim.visible = false;
    this.root.add(this.dim);

    this.detailA = new THREE.Mesh(quadGeo(), new THREE.MeshBasicMaterial({ transparent: true, alphaTest: 0.05 }));
    this.detailB = new THREE.Mesh(quadGeo(), new THREE.MeshBasicMaterial({ transparent: true, alphaTest: 0.05 }));
    for (const m of [this.detailA, this.detailB]) {
      m.renderOrder = 30;
      m.visible = false;
      this.root.add(m);
    }

    // 늦게 도착한 텍스처는 **다시 고르게만** 한다 — 어느 홀더·어느 상세 카드가 그 id 를
    // 지금 갖고 있는지는 assign / applyDetailTexture 가 그 프레임에 다시 본다.
    // (도착한 것을 붙잡아 뒀다가 입히면 그 사이에 면이 넘어간 경우 엉뚱한 카드에 붙는다.)
    this.cardCache.onArrive = (id, okArrived, tier) => {
      this.texVersion++;
      if (okArrived && tier === 'detail') this.onDetailTex?.(id);
    };
    this.root.visible = false;
  }

  private detailStamp = '';

  // ---------------------------------------------------------------- 외부
  setCounts(counts: Record<string, number>): void {
    this.counts = counts;
    this.countsVersion++;
  }
  invalidate(): void {
    this.countsVersion++;
  }

  countOf(def: CardDef | undefined, slot: number): number {
    if (!def) return 0;
    const n = this.counts[def.id] ?? 0;
    return Math.max(0, n - (this.pendingStow.get(slot) ?? 0));
  }

  fallbackCardTex(def: CardDef): THREE.Texture {
    let t = this.fallbackCards.get(def.rank);
    if (!t) {
      t = fallbackFaceCard(def.rank, RANK_LABEL[def.rank] ?? '?');
      this.fallbackCards.set(def.rank, t);
    }
    return t;
  }
  private fallbackDetailTex(def: CardDef): THREE.Texture {
    let t = this.fallbackDetails.get(def.id);
    if (!t) {
      if (this.fallbackDetails.size >= FALLBACK_DETAIL_MAX) {
        const oldest = this.fallbackDetails.keys().next().value as string;
        this.fallbackDetails.get(oldest)!.dispose();
        this.fallbackDetails.delete(oldest);
      }
      t = fallbackDetailCard(def.rank, def.name ?? def.id);
      this.fallbackDetails.set(def.id, t);
    }
    return t;
  }
  badgeTex(count: number): THREE.Texture {
    let t = this.badgeCache.get(count);
    if (!t) {
      t = makeBadgeTexture(count);
      this.badgeCache.set(count, t);
    }
    return t;
  }

  /** 손가락이 칸에 닿는 순간 (탭 확정 전) 원본을 미리 부른다. */
  prefetchDetail(slot: number): void {
    const def = cardBySlot(slot);
    if (def) this.cardCache.requestDetail(def.id);
  }

  setTexture(name: string, t: THREE.Texture): boolean {
    switch (name) {
      case 'binder_cover':
        this.tex.binderCover = t;
        (this.cover.material as THREE.ShaderMaterial).uniforms.map.value = t;
        this.realTex.cover = true;
        return true;
      case 'binder_sheet':
        this.tex.binderSheet = t;
        for (const s of [this.left, this.right, this.turning]) s.front.setSheetTexture(t);
        this.realTex.sheet = true;
        return true;
      case 'binder_sheet_back':
        this.tex.binderSheetBack = t;
        for (const s of [this.left, this.right, this.turning]) s.back.setSheetTexture(t);
        this.realTex.sheetBack = true;
        return true;
      case 'binder_gloss':
        this.tex.binderGloss = t;
        for (const s of [this.left, this.right, this.turning]) s.front.setGlossTexture(t);
        this.realTex.gloss = true;
        return true;
      case 'binder_gloss_back':
        this.tex.binderGlossBack = t;
        for (const s of [this.left, this.right, this.turning]) s.back.setGlossTexture(t);
        this.realTex.glossBack = true;
        return true;
      default:
        return false;
    }
  }

  /** 측정 좌표(binder.json)가 도착했을 때. **다시 배치**해야 카드가 슬리브 밖에 안 걸린다. */
  setLayout(): void {
    // 아직 폴백 그림을 쓰고 있으면 새 좌표로 다시 그린다
    if (!this.realTex.cover) this.setTextureLocal('cover', fallbackBinderCover());
    if (!this.realTex.sheet) this.setTextureLocal('sheet', fallbackBinderSheet(false));
    if (!this.realTex.sheetBack) this.setTextureLocal('sheetBack', fallbackBinderSheet(true));
    if (!this.realTex.gloss) this.setTextureLocal('gloss', fallbackBinderGloss(false));
    if (!this.realTex.glossBack) this.setTextureLocal('glossBack', fallbackBinderGloss(true));
    this.lastW = -1; // 다음 draw 에서 relayout
    this.countsVersion++;
  }

  private setTextureLocal(kind: keyof typeof this.realTex, t: THREE.Texture): void {
    const name = { cover: 'binder_cover', sheet: 'binder_sheet', sheetBack: 'binder_sheet_back', gloss: 'binder_gloss', glossBack: 'binder_gloss_back' }[kind];
    const was = this.realTex[kind];
    this.setTexture(name, t);
    this.realTex[kind] = was;
  }

  private relayout(W: number, H: number): void {
    for (const s of [this.left, this.right, this.turning]) s.layout(W, H, this.box);
    const cm = BINDER_LAYERS.coverMargin;
    // 커버는 시트 뒤를 채우는 배경이다. 폭을 "펼친 두 면"으로 잡으면 카메라가 한 면을
    // 중앙에 두는 순간 반대편 커버가 화면 절반을 먹는다 (가로로 넓은 창에서 특히).
    // 실제 위치는 draw() 에서 카메라(viewX)를 따라가게 매 프레임 정한다.
    this.cover.scale.set(2 * (W + cm) + P.binder.neighborPeek, H + 2 * cm, 1);
    this.cover.position.set(0, P.binder.faceY, BINDER_LAYERS.cover);
    this.lastW = W;
    this.lastH = H;
    this.lastMeta = binderMetaVersion();
    this.countsVersion++;
  }

  // ---------------------------------------------------------------- 그리기
  draw(b: BinderRS, aspect: number): void {
    this.root.visible = true;
    const W = faceWidth(aspect);
    const H = faceHeight(aspect);
    if (W !== this.lastW || H !== this.lastH || binderMetaVersion() !== this.lastMeta) this.relayout(W, H);

    // 펼침(와이드)에서는 viewX 가 항상 0 이다 — 펼침 전체가 화면에 들어오므로 카메라가
    // 면 단위로 움직일 이유가 없다. **u 의 함수라는 점은 그대로다** (상수 함수).
    // 역할 배정(sLeft/sRight)은 아래에서 한 줄도 안 바뀐다: floor(face/2) 가 2k 와 2k+1 에
    // 같은 값이라 펼침의 좌우 면이 그대로 나온다.
    const viewX = viewXOf(b.segKind as 0 | 1 | 2 | 3, b.segDir, b.u, b.face, W, isSpread(aspect));
    this.root.position.x = -viewX;
    this.root.position.y = b.riseY;

    // **커버는 바인더 본체다 — 카메라를 따라가지 않는다.**
    // 예전엔 로컬 x = viewX 로 두어 화면 중앙에 박아 놨는데, 그러면 넘길 때
    // 시트만 돌고 배경(커버)은 가만히 있어서 "책이 안 움직인다" 로 보인다.
    // 실물은 책 전체가 좌우로 밀린다 → 커버를 루트에 맡기고 로컬 x 는 0 으로 둔다.
    //
    // 펼침(와이드)은 viewX 가 0 이라 어차피 차이가 없다.
    // 포트레이트는 한쪽 면만 보이므로 커버의 **등(spine)이 화면 왼쪽**에 와야 한다
    // (오른쪽 반쪽만 펼쳐 든 모습). 그 정렬은 relayout 에서 커버 중심을 옮겨 맞춘다.
    this.cover.position.x = 0;

    // ---- 수납 중이면 아직 안 내려앉은 카드를 칸에서 뺀다 (배지 숫자가 착지할 때마다 오른다)
    const wantPending = b.mode === 0;
    if (wantPending || this.pendingStow.size) {
      let changed = false;
      const next = new Map<number, number>();
      if (wantPending) {
        for (let i = 0; i < CARD_COUNT; i++) {
          const slot = b.sslot[i] | 0;
          if (slot > 0 && b.slanded[i] < 0.5) next.set(slot, (next.get(slot) ?? 0) + 1);
        }
      }
      if (next.size !== this.pendingStow.size) changed = true;
      else for (const [k, v] of next) if (this.pendingStow.get(k) !== v) changed = true;
      if (changed) {
        this.pendingStow = next;
        this.countsVersion++;
      }
    }

    // ---- 역할 배정 (§7.8)
    let sTurn = 0, sLeft = 0, sRight = 0;
    if (b.segKind === SEG_FLIP) {
      sTurn = b.segSheet;
      sLeft = sTurn - 1;
      sRight = sTurn + 1;
    } else {
      const fRef = b.segKind === SEG_PAN ? Math.min(b.segFrom, b.segTo) : b.face;
      sLeft = Math.floor(fRef / 2);
      sRight = sLeft + 1;
    }
    this.left.assign(sLeft, this);
    this.right.assign(sRight, this);
    if (sTurn > 0) this.turning.assign(sTurn, this);
    else this.turning.group.visible = false;

    // 더미 두께 — 넘긴 시트가 쌓일수록 왼쪽이 두꺼워진다
    const S = sheetCount();
    const th = P.binder.sheetThickness;
    this.left.group.position.z = th * Math.max(0, sLeft);
    this.right.group.position.z = th * Math.max(0, S - sRight + 1);
    // 더미 꼭대기 — 시트 그룹 z + 그 안의 최상위 레이어(gloss). 카드가 늘면 S 가 커지므로
    // **상수로 박으면 안 된다** (2026-09-16 z-파이팅). 위에 오는 것들은 전부 여기서 유도한다.
    const stackTop =
      Math.max(this.left.group.position.z, this.right.group.position.z) + BINDER_LAYERS.gloss;
    this.left.group.rotation.y = -Math.PI;
    this.right.group.rotation.y = 0;

    if (sTurn > 0) {
      const theta = thetaOf(b.u, b.segDir);
      this.turning.group.rotation.y = -theta;
      this.turning.group.position.z = stackTop + BINDER_LAYERS.turningLift;
      // 광택 밴드는 도는 시트에만. 시트를 들어올릴 때 빛이 쓸려 지나간다.
      const band = 0.5 - 0.9 * Math.sin(theta);
      this.turning.front.setBand(band, P.binder.glossBand);
      this.turning.back.setBand(band, P.binder.glossBand);
    }
    this.left.front.setBand(-10, 0);
    this.left.back.setBand(-10, 0);
    this.right.front.setBand(-10, 0);
    this.right.back.setBand(-10, 0);

    // ---- 카드 그림 창 (요청·해제는 스텝 밖 = 여기)
    // 아틀라스가 없어졌으니 창도 **카드 단위**다: 현재 면 ±cellTexWindow 안에서 **보유한** 카드만.
    // 미보유 칸은 비어 있고 상세 보기도 뒷면을 보여주므로 그림을 받을 이유가 없다.
    const center = b.face;
    const wstamp = `${center}|${this.countsVersion}`;
    if (wstamp !== this.windowStamp) {
      this.windowStamp = wstamp;
      this.windowIds.clear();
      const F = faceCount();
      const N = Math.max(0, P.binder.cellTexWindow | 0);
      for (let d = -N; d <= N; d++) {
        const f = center + d;
        if (f < 1 || f > F) continue;
        for (let i = 0; i < CELLS_PER_FACE; i++) {
          const slot = faceToSlot(f, i);
          const def = cardBySlot(slot);
          if (!def || this.countOf(def, slot) <= 0) continue;
          this.windowIds.add(def.id);
          this.cardCache.requestCell(def.id);
        }
      }
    }
    // **매 프레임** 정리한다. 도착이 비동기라 창이 바뀔 때만 비우면 날아오던 것들이 상한을 넘긴다.
    // 상한 안이면 바로 빠져나오므로 비용은 비교 한 번이다.
    this.cardCache.evictCells(this.windowIds, Math.max(CELLS_PER_FACE, P.binder.cellTexMax | 0));

    // ---- 수납 카드
    for (let i = 0; i < CARD_COUNT; i++) {
      const q = this.stowQuads[i];
      const on = b.svis[i] > 0.5;
      q.visible = on;
      if (!on) continue;
      const slot = b.sslot[i] | 0;
      const def = cardBySlot(slot);
      const mat = q.material as THREE.MeshBasicMaterial;
      if (def) setMap(mat, this.cardCache.requestCell(def.id) ?? this.fallbackCardTex(def));
      q.scale.set(b.ss[i], b.ss[i] * CARD_ASPECT, 1);
      q.position.set(b.sx[i], b.sy[i], stackTop + BINDER_LAYERS.stowCard);
    }

    // ---- 디밍 + 상세 카드
    const dimOn = b.dim > 0.002;
    this.dim.visible = dimOn;
    if (dimOn) {
      (this.dim.material as THREE.MeshBasicMaterial).opacity = b.dim;
      this.dim.scale.set(screenWidth(aspect) * 1.2, WORLD.viewH * 1.2, 1);
      this.dim.position.set(viewX, 0, stackTop + BINDER_LAYERS.dim);
    }
    const detOn = b.detActive > 0.5;
    this.detailA.visible = detOn;
    this.detailB.visible = detOn && b.detBVis > 0.5;
    if (detOn) {
      const stamp = `${b.detSlotA}|${b.detSlotB}|${b.detOwnedA}|${b.detOwnedB}|${this.countsVersion}|${this.texVersion}`;
      if (stamp !== this.detailStamp) {
        this.detailStamp = stamp;
        this.detailSourceA = this.applyDetailTexture(this.detailA, b.detSlotA | 0, b.detOwnedA > 0.5);
        if (this.detailB.visible) this.applyDetailTexture(this.detailB, b.detSlotB | 0, b.detOwnedB > 0.5);
      }
      this.detailA.scale.set(b.as, b.as * CARD_ASPECT, 1);
      this.detailA.position.set(b.ax, b.ay, b.az);
      this.detailB.scale.set(b.bs, b.bs * CARD_ASPECT, 1);
      this.detailB.position.set(b.bx, b.by, b.bz);
      // 상세 보기에는 수량 배지를 붙이지 않는다. 칸에 있을 때 이미 보이고,
      // 확대된 카드까지 따라오면 그림을 가린다 (사용자 요청 2026-09-10).
    }
  }

  /**
   * 원본 → (원본이 오는 동안) 칸 텍스처 → 폴백 / 미보유는 뒷면.
   * **미보유에는 그림을 요청하지 않는다** (스포일러).
   * 원본과 칸이 같은 파일이라 원본은 **디코딩만 다시** 한다 — 파일을 다시 받지는 않는다.
   */
  private applyDetailTexture(mesh: THREE.Mesh, slot: number, owned: boolean): DetailTexSource {
    const mat = mesh.material as THREE.MeshBasicMaterial;
    const def = cardBySlot(slot);
    if (!def) {
      setMap(mat, this.tex.cardBack);
      mat.color.setScalar(1);
      return 'none';
    }
    if (!owned) {
      setMap(mat, this.tex.cardBack);
      mat.color.setScalar(1 - P.binder.detailUnownedDim);
      return 'back';
    }
    mat.color.setScalar(1);
    const orig = this.cardCache.requestDetail(def.id);
    if (orig) {
      setMap(mat, orig);
      return 'original';
    }
    const cell = this.cardCache.missed(def.id) ? null : this.cardCache.requestCell(def.id);
    if (cell) {
      setMap(mat, cell);
      return 'cell';
    }
    setMap(mat, this.fallbackDetailTex(def));
    return 'fallback';
  }

  /** 상세 보기가 닫히고 정착했을 때 (스텝 밖). */
  evictDetail(): void {
    this.cardCache.evictDetails(P.binder.detailTexMax);
  }

  /** 확대된 카드의 화면상 폭 (진단·검증용). */
  detailScreenWidth(b: BinderRS): number {
    return b.as * perspScale(b.az);
  }
}
